import React, { useState, useEffect, useCallback, useRef } from 'react';
import clsx from 'clsx';
import FooterNav from './components/layout/FooterNav';
import { ProductsTab } from './components/ProductsTab';
import { OrdersTab } from './components/OrdersTab';
import { CouponsTab } from './components/CouponsTab';
import { CategoriesTab } from './components/CategoriesTab';
import { SettingsTab } from './components/SettingsTab';
import { DeliveryTab } from './components/DeliveryTab';
import { TimeSlotsTab } from './components/TimeSlotsTab';
import { WelcomePopup } from './components/WelcomePopup';
import { StoreDetailsTab } from './components/StoreDetailsTab';
import { AnalyticsTab } from './components/AnalyticsTab';
import { useStore, useStoreProducts, useStoreCategories } from './hooks/useStoreApi';
import { getVocab } from './utils/storeVocabulary';
import { OfferingTypeProvider } from './hooks/useVocab';
import { Package, ShoppingCart, Tag, BarChart3, Ticket, CreditCard, MapPin, Store, Clock, CalendarDays, CalendarClock, Sparkles } from 'lucide-react';
import { navigate } from './App';
import { Spinner } from './components/ui/Spinner';
import {
    StoreAdminNav,
    RefreshFab,
    DashboardTab,
    CommandPalette,
} from './components/store/admin';

const ADMIN_TABS = [
    { id: 'dashboard',     label: 'סקירה',         icon: BarChart3 },
    { id: 'analytics',     label: 'ניתוח נתונים',  icon: BarChart3 },
    { id: 'store-details', label: 'הגדרות חנות',   icon: Store },
    { id: 'products',      label: 'מוצרים',        icon: Package },
    { id: 'orders',        label: 'הזמנות',        icon: ShoppingCart },
    { id: 'categories',    label: 'קטגוריות',      icon: Tag },
    { id: 'coupons',       label: 'קופונים',       icon: Ticket },
    { id: 'delivery',      label: 'חלוקה',         icon: MapPin },
    { id: 'timeslots',     label: 'משבצות זמן',    icon: Clock },
    { id: 'settings',      label: 'תשלומים',       icon: CreditCard },
];

/**
 * Build the admin tab list for a given offering type. A shop and a practice
 * share the same tabs underneath but speak differently and prioritize
 * differently: a practice leads with its bookings, services and availability,
 * and has no physical delivery.
 */
const buildAdminTabs = (offeringType, t) => {
    const appt = offeringType === 'appointments';

    const meta = {
        dashboard:       { label: 'סקירה',                                   icon: BarChart3 },
        analytics:       { label: 'ניתוח נתונים',                            icon: BarChart3 },
        'store-details': { label: 'הגדרות חנות',                            icon: Store },
        products:        { label: t.catalog,                                 icon: appt ? Sparkles : Package },
        orders:          { label: t.dashboardOrdersTab,                      icon: appt ? CalendarDays : ShoppingCart },
        categories:      { label: 'קטגוריות',                                icon: Tag },
        coupons:         { label: 'קופונים',                                 icon: Ticket },
        delivery:        { label: 'חלוקה',                                   icon: MapPin },
        timeslots:       { label: appt ? 'זמינות' : 'משבצות זמן',           icon: appt ? CalendarClock : Clock },
        settings:        { label: 'תשלומים',                                 icon: CreditCard },
    };

    // Order + visibility per personality. Practice leads with bookings.
    const order = appt
        ? ['dashboard', 'orders', 'products', 'timeslots', 'categories', 'coupons', 'store-details', 'analytics', 'settings']
        : ['dashboard', 'analytics', 'store-details', 'products', 'orders', 'categories', 'coupons', 'delivery', 'timeslots', 'settings'];

    return order.map(id => ({ id, label: meta[id].label, icon: meta[id].icon }));
};

/**
 * StoreAdmin — thin coordinator. Owns:
 *   - the active tab
 *   - shared store/products/categories data + their refetchers
 *   - the per-tab refresh-handler registry (used by the FAB)
 * Delegates rendering to the smaller pieces in `components/store/admin/`.
 */
const VALID_TAB_IDS = new Set(ADMIN_TABS.map(t => t.id));

const initialTabFromUrl = () => {
    if (typeof window === 'undefined') return 'dashboard';
    const tab = new URLSearchParams(window.location.search).get('tab');
    return tab && VALID_TAB_IDS.has(tab) ? tab : 'dashboard';
};

const SIDEBAR_COLLAPSED_KEY = 'store_admin_sidebar_collapsed';

const initialSidebarCollapsed = () => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
};

export const StoreAdmin = ({ storeSlug, chatId }) => {
    const [activeTab, setActiveTab] = useState(initialTabFromUrl);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [showWelcomePopup, setShowWelcomePopup] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(initialSidebarCollapsed);
    const [commandOpen, setCommandOpen] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    // pendingIntent gets set when the AI chat (or command palette) wants the
    // active tab to take a specific follow-up action — e.g. "open create
    // modal" right after we land on the Coupons tab. Tabs consume it via
    // their `intent` prop and call onIntentConsumed to clear it.
    const [pendingIntent, setPendingIntent] = useState(null);

    useEffect(() => {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed));
    }, [sidebarCollapsed]);

    // Flag the global chat bar that we're inside the admin so it can offer
    // admin-flavored starter prompts. Cleared on unmount.
    useEffect(() => {
        window.__mekomit_admin_active = true;
        return () => {
            window.__mekomit_admin_active = false;
            window.__mekomit_admin_context = null;
        };
    }, []);

    // Listen for AI-chat navigation requests. The chat dispatches this when
    // the assistant emits a [GO:tab=...,action=...] marker and the user
    // clicks the resulting button.
    useEffect(() => {
        const onAdminNavigate = (e) => {
            const detail = e?.detail || {};
            const tab = detail.tab;
            const action = detail.action || null;
            if (!tab || !VALID_TAB_IDS.has(tab)) return;
            setActiveTab(tab);
            setShowMobileMenu(false);
            setCommandOpen(false);
            if (action) setPendingIntent({ tab, action });
        };
        window.addEventListener('mekomit:admin-navigate', onAdminNavigate);
        return () => window.removeEventListener('mekomit:admin-navigate', onAdminNavigate);
    }, []);

    const { data: store, loading: storeLoading, refetch: refetchStore } = useStore(storeSlug, chatId);
    const { data: products, refetch: refetchProducts } = useStoreProducts(storeSlug);
    const { data: categories, refetch: refetchCategories } = useStoreCategories(storeSlug);

    // Keep the global chat bar's admin-context snapshot in sync. Used by
    // AIChatBar to send store data along with admin questions so the back-end
    // can answer with the actual catalogue / settings instead of guessing.
    useEffect(() => {
        if (!store) return;
        const compactProducts = Array.isArray(products)
            ? products.slice(0, 80).map((p) => ({
                id: p.product_id,
                name: p.name,
                price: p.price,
                category_id: p.category_id,
                category: p.category_name,
                available: p.available !== false,
                has_image: !!(p.image_url || p.image || (p.images && p.images.length)),
                description: typeof p.description === 'string' ? p.description.slice(0, 240) : undefined,
            }))
            : [];
        const compactCategories = Array.isArray(categories)
            ? categories.map((c) => ({ id: c.category_id, name: c.name, description: c.description }))
            : [];
        window.__mekomit_admin_context = {
            store: {
                store_id: store.store_id,
                slug: store.slug || storeSlug,
                name: store.name,
                about: store.about,
                has_logo: !!(store.owner_profile_url || store.profile_image),
                has_cover: !!(store.cover_image_url || store.cover_image),
                has_about_image: !!(store.about_image_url || store.about_image),
                contact_phone: store.contact_phone,
                purchase_confirmation_message: store.purchase_confirmation_message,
                cancellation_policy: store.cancellation_policy,
                is_closed: !!(store.is_closed || store.closed),
            },
            products: compactProducts,
            products_total: Array.isArray(products) ? products.length : 0,
            categories: compactCategories,
        };
    }, [store, products, categories, storeSlug]);

    // Refresh registry
    const refreshHandlersRef = useRef({});
    const [refreshing, setRefreshing] = useState(false);
    const [refreshStatus, setRefreshStatus] = useState('');
    const [toast, setToast] = useState(null);

    const registerRefresh = useCallback((tabId, handler) => {
        if (typeof handler === 'function') refreshHandlersRef.current[tabId] = handler;
    }, []);

    const triggerRefresh = useCallback(async () => {
        if (refreshing) return;
        const handler = refreshHandlersRef.current[activeTab];
        if (!handler) return;
        try {
            setRefreshing(true);
            setRefreshStatus('Refreshing');
            setToast(null);
            await handler();
            setRefreshStatus('Updated');
            setToast({ type: 'success', message: 'עודכן' });
            setTimeout(() => { setRefreshStatus(''); setToast(null); }, 3000);
        } catch (err) {
            setRefreshStatus("Couldn't refresh. Try again.");
            setToast({ type: 'error', message: "Couldn't refresh. Try again." });
            setTimeout(() => setRefreshStatus(''), 5000);
        } finally {
            setRefreshing(false);
        }
    }, [activeTab, refreshing]);

    // Global admin shortcuts: Alt+R refresh, Ctrl/Cmd+K command palette, ? shortcut help
    useEffect(() => {
        const isTypingTarget = (el) => {
            if (!el) return false;
            const tag = el.tagName;
            return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
        };
        const onKey = (e) => {
            // Ctrl/Cmd+K — open command palette
            if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault();
                setCommandOpen((v) => !v);
                return;
            }
            // Alt+R — refresh active tab
            if (e.altKey && (e.key === 'r' || e.key === 'R')) {
                e.preventDefault();
                triggerRefresh();
                return;
            }
            // "?" — show keyboard shortcuts (only when not typing)
            if (!isTypingTarget(e.target) && (e.key === '?' || (e.shiftKey && e.key === '/'))) {
                e.preventDefault();
                setShortcutsOpen(true);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [triggerRefresh]);

    // Access control
    const isOwner = store?.is_owner === true;
    const isCoAdmin = store?.is_co_admin === true;
    const canAccessAdmin = isOwner || isCoAdmin;
    const isActualOwner = isOwner && !isCoAdmin;

    useEffect(() => {
        if (!storeLoading && store && !canAccessAdmin) {
            navigate('store', `slug=${storeSlug}`);
        }
    }, [storeLoading, store, canAccessAdmin, storeSlug]);

    // First-visit welcome popup
    useEffect(() => {
        if (storeLoading || !store || !canAccessAdmin || !store.store_id) return;
        const visitKey = `store_admin_visited_${store.store_id}`;
        if (!localStorage.getItem(visitKey)) {
            setShowWelcomePopup(true);
            localStorage.setItem(visitKey, 'true');
        }
    }, [storeLoading, store, canAccessAdmin]);

    const openStorePublic = () => {
        if (!storeSlug) return;
        const cleanSlug = encodeURIComponent(storeSlug.replace(/^\//, ''));
        window.open(`/${cleanSlug}`, '_blank', 'noopener,noreferrer');
    };

    if (storeLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-surface-base">
                <div className="text-center space-y-3">
                    <Spinner size="xl" />
                    <p className="text-ink-muted">טוען...</p>
                </div>
            </div>
        );
    }

    if (!canAccessAdmin) return null;

    const storeId = store?.store_id;
    const offeringType = store?.offering_type;
    const t = getVocab(offeringType);
    const tabs = buildAdminTabs(offeringType, t);

    return (
        <OfferingTypeProvider offeringType={offeringType}>
        <div className="min-h-screen flex flex-col bg-surface-base text-ink" dir="rtl">
            <StoreAdminNav
                tabs={tabs}
                activeTab={activeTab}
                onChangeTab={setActiveTab}
                onOpenStorePublic={openStorePublic}
                store={store}
                showMobileMenu={showMobileMenu}
                setShowMobileMenu={setShowMobileMenu}
                collapsed={sidebarCollapsed}
                onToggleCollapsed={() => setSidebarCollapsed(v => !v)}
            />

            <main
                className={clsx(
                    'flex-1 w-full max-w-7xl mx-auto px-4 py-6 pb-24 pt-20 transition-[padding] duration-200',
                    sidebarCollapsed ? 'lg:ps-20' : 'lg:ps-60',
                )}
            >
                <div className="animate-fade-in">
                    {activeTab === 'dashboard' && (
                        <DashboardTab
                            store={store}
                            products={products}
                            categories={categories}
                            refetchStore={refetchStore}
                            refetchProducts={refetchProducts}
                            refetchCategories={refetchCategories}
                            onRegisterRefresh={registerRefresh}
                            chatId={chatId}
                            onGoToTab={setActiveTab}
                        />
                    )}
                    {activeTab === 'products' && (
                        <ProductsTab
                            storeId={storeId}
                            store={store}
                            products={products}
                            categories={categories}
                            refetch={refetchProducts}
                            onRegisterRefresh={registerRefresh}
                            chatId={chatId}
                            intent={pendingIntent?.tab === 'products' ? pendingIntent.action : null}
                            onIntentConsumed={() => setPendingIntent(null)}
                        />
                    )}
                    {activeTab === 'orders' && (
                        <OrdersTab storeId={storeId} storeSlug={storeSlug} onRegisterRefresh={registerRefresh} chatId={chatId} />
                    )}
                    {activeTab === 'analytics' && (
                        <AnalyticsTab storeId={storeId} onRegisterRefresh={registerRefresh} chatId={chatId} />
                    )}
                    {activeTab === 'categories' && (
                        <CategoriesTab
                            storeId={storeId}
                            categories={categories}
                            refetch={refetchCategories}
                            onRegisterRefresh={registerRefresh}
                            chatId={chatId}
                            intent={pendingIntent?.tab === 'categories' ? pendingIntent.action : null}
                            onIntentConsumed={() => setPendingIntent(null)}
                        />
                    )}
                    {activeTab === 'coupons' && (
                        <CouponsTab
                            storeId={storeId}
                            onRegisterRefresh={registerRefresh}
                            chatId={chatId}
                            intent={pendingIntent?.tab === 'coupons' ? pendingIntent.action : null}
                            onIntentConsumed={() => setPendingIntent(null)}
                        />
                    )}
                    {activeTab === 'delivery' && (
                        <DeliveryTab storeId={storeId} storeSlug={storeSlug} onRegisterRefresh={registerRefresh} chatId={chatId} />
                    )}
                    {activeTab === 'timeslots' && (
                        <TimeSlotsTab storeId={storeId} onRegisterRefresh={registerRefresh} chatId={chatId} />
                    )}
                    {activeTab === 'store-details' && (
                        <StoreDetailsTab
                            store={store}
                            refetchStore={refetchStore}
                            onRegisterRefresh={registerRefresh}
                            chatId={chatId}
                            isActualOwner={isActualOwner}
                        />
                    )}
                    {activeTab === 'settings' && (
                        <SettingsTab storeId={storeId} onRegisterRefresh={registerRefresh} chatId={chatId} />
                    )}
                </div>
            </main>

            <RefreshFab
                refreshing={refreshing}
                refreshStatus={refreshStatus}
                onRefresh={triggerRefresh}
                toast={toast}
            />

            <FooterNav chatId={chatId} />

            {showWelcomePopup && store && (
                <WelcomePopup storeName={store.name} onClose={() => setShowWelcomePopup(false)} />
            )}

            <CommandPalette
                open={commandOpen}
                onClose={() => setCommandOpen(false)}
                tabs={tabs}
                products={products}
                onGoToTab={setActiveTab}
                onRefresh={triggerRefresh}
                onOpenStorePublic={openStorePublic}
                onShowShortcuts={() => setShortcutsOpen(true)}
            />

            {shortcutsOpen && (
                <ShortcutsModal onClose={() => setShortcutsOpen(false)} />
            )}
        </div>
        </OfferingTypeProvider>
    );
};

/**
 * Lightweight shortcuts cheatsheet. Rendered conditionally so it doesn't
 * cost anything until invoked.
 */
function ShortcutsModal({ onClose }) {
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prev;
            document.removeEventListener('keydown', onKey);
        };
    }, [onClose]);

    const rows = [
        { keys: ['Ctrl', 'K'],     label: 'פתיחה מהירה — לשוניות, פעולות, מוצרים' },
        { keys: ['Alt', 'R'],      label: 'רענון הלשונית הנוכחית' },
        { keys: ['?'],             label: 'הצגת חלון זה' },
        { keys: ['Esc'],           label: 'סגירת חלון או חיפוש' },
        { keys: ['↑', '↓', '↵'],   label: 'ניווט ובחירה בפתיחה המהירה' },
    ];

    return (
        <div
            className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-ink/45 backdrop-blur-[3px] animate-fade-in"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="קיצורי מקלדת"
        >
            <div
                className="bg-surface rounded-2xl w-full max-w-md shadow-elevated border border-line-subtle animate-scale-in"
                onClick={(e) => e.stopPropagation()}
                dir="rtl"
            >
                <div className="px-5 py-4 border-b border-line-subtle">
                    <div className="text-2xs font-bold tracking-wider text-ink-faint uppercase">עזרה</div>
                    <h3 className="text-lg font-bold text-ink-strong mt-0.5">קיצורי מקלדת</h3>
                </div>
                <div className="px-5 py-4 space-y-2.5">
                    {rows.map((r, i) => (
                        <div key={i} className="flex items-center justify-between gap-3">
                            <span className="text-sm text-ink-muted leading-relaxed">{r.label}</span>
                            <span className="flex items-center gap-1 shrink-0">
                                {r.keys.map((k, j) => (
                                    <React.Fragment key={j}>
                                        {j > 0 && <span className="text-2xs text-ink-faint">+</span>}
                                        <kbd className="px-2 py-1 text-2xs font-semibold rounded bg-surface-muted border border-line text-ink-strong tabular-nums">
                                            {k}
                                        </kbd>
                                    </React.Fragment>
                                ))}
                            </span>
                        </div>
                    ))}
                </div>
                <div className="px-5 py-3 border-t border-line-subtle bg-surface-muted/40 flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg font-semibold text-sm bg-brand-500 hover:bg-brand-600 text-ink-inverse transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                    >
                        סגור
                    </button>
                </div>
            </div>
        </div>
    );
}

export default StoreAdmin;
